import { Component } from '@theme/component';
import { onAnimationEnd, prefersReducedMotion } from '@theme/utilities';
import { ZoomMediaSelectedEvent } from '@theme/events';
import { DialogOpenEvent, DialogCloseEvent } from '@theme/dialog';

/** Full-screen product photography with zoom, pan and image navigation. */
export class ZoomDialog extends Component {
  requiredRefs = ['dialog', 'media', 'gallery', 'stage', 'toolbar', 'status'];
  #index = 0;
  #scale = 1;
  #x = 0;
  #y = 0;
  #expanded = false;
  #closing = false;
  #opener;
  #bodyState;
  #controller;
  #pointers = new Map();
  #gesture;
  #frame;
  #wrapSlide;
  #wrapTimer;
  #wrapping = false;

  connectedCallback() {
    super.connectedCallback();
    this.#controller = new AbortController();
    const { signal } = this.#controller;
    const { dialog, stage } = this.refs;
    dialog.addEventListener('cancel', this.#cancel, { signal });
    stage.addEventListener('pointerdown', this.#pointerDown, { signal });
    stage.addEventListener('pointermove', this.#pointerMove, { signal });
    stage.addEventListener('pointerup', this.#pointerUp, { signal });
    stage.addEventListener('pointercancel', this.#pointerCancel, { signal });
    stage.addEventListener('dragstart', (event) => event.preventDefault(), { signal });
    window.addEventListener('resize', this.#resize, { signal });
    this.refs.media.forEach((slide) => {
      slide.querySelector('img')?.addEventListener('load', this.#resize, { signal });
    });
  }

  disconnectedCallback() {
    this.#controller?.abort();
    cancelAnimationFrame(this.#frame);
    this.#finishWrap();
    if (this.#bodyState) this.closeDialog();
    super.disconnectedCallback();
  }

  open(index, event) {
    event?.preventDefault();
    const { dialog } = this.refs;
    if (dialog.open || !this.refs.media.length) return;
    const target = event?.target instanceof Element ? event.target : null;
    this.#opener = target?.closest('button, a, [tabindex]') || target?.closest('li, slideshow-slide') || document.activeElement;
    this.#bodyState = {
      y: window.scrollY,
      width: document.body.style.width,
      position: document.body.style.position,
      top: document.body.style.top,
    };
    document.body.style.width = '100%';
    document.body.style.position = 'fixed';
    document.body.style.top = '-' + this.#bodyState.y + 'px';
    this.#closing = false;
    dialog.classList.remove('gang-zoom-closing');
    dialog.showModal();
    this.#select(Number(index) || 0, false);
    dialog.querySelector('.gang-zoom-close')?.focus({ preventScroll: true });
    this.dispatchEvent(new DialogOpenEvent());
  }

  async close() {
    if (!this.refs.dialog.open || this.#closing) return;
    this.#closing = true;
    this.refs.dialog.classList.add('gang-zoom-closing');
    if (!prefersReducedMotion()) await onAnimationEnd(this.refs.dialog, undefined, { subtree: false });
    this.closeDialog();
  }

  closeDialog() {
    const { dialog } = this.refs;
    this.#finishWrap();
    this.#pointers.clear();
    this.#gesture = null;
    dialog.removeAttribute('data-dragging');
    dialog.close();
    dialog.classList.remove('gang-zoom-closing');
    if (this.#bodyState) {
      const { y, width, position, top } = this.#bodyState;
      document.body.style.width = width;
      document.body.style.position = position;
      document.body.style.top = top;
      window.scrollTo({ top: y, behavior: 'instant' });
      this.#bodyState = null;
    }
    this.#pauseMedia();
    if (this.#opener instanceof HTMLElement && this.#opener.isConnected) {
      const needsTabIndex = !this.#opener.matches('button, a, input, [tabindex]');
      if (needsTabIndex) this.#opener.setAttribute('tabindex', '-1');
      this.#opener.focus({ preventScroll: true });
      if (needsTabIndex) this.#opener.removeAttribute('tabindex');
    }
    this.#closing = false;
    window.dispatchEvent(new DialogCloseEvent());
  }

  #cancel = (event) => { event.preventDefault(); this.close(); };
  #resize = () => {
    if (!this.refs.dialog.open) return;
    this.#clamp();
    this.#paint(false);
  };

  #pauseMedia() {
    this.refs.dialog.querySelectorAll('video').forEach((video) => video.pause());
    this.refs.dialog.querySelectorAll('deferred-media').forEach((media) => media.pause?.());
  }

  #onWrapTransitionEnd = (event) => {
    if (event.target === this.refs.gallery && event.propertyName === 'transform') this.#finishWrap();
  };

  #finishWrap() {
    if (!this.#wrapping) return;
    const { gallery } = this.refs;
    gallery.removeEventListener('transitionend', this.#onWrapTransitionEnd);
    clearTimeout(this.#wrapTimer);
    gallery.style.transitionDuration = '0ms';
    gallery.style.transform = 'translate3d(' + (-this.#index * 100) + '%,0,0)';
    if (this.#wrapSlide) this.#wrapSlide.style.transform = '';
    gallery.getBoundingClientRect();
    gallery.style.transitionDuration = '';
    this.#wrapSlide = undefined;
    this.#wrapping = false;
  }

  #select(index, animate = true, visualIndex = null) {
    const { gallery, media, dialog } = this.refs;
    this.#pauseMedia();
    this.#index = (index + media.length) % media.length;
    this.#scale = 1;
    this.#x = this.#y = 0;
    this.#expanded = false;
    this.#pointers.clear();
    this.#gesture = null;
    dialog.removeAttribute('data-dragging');
    gallery.style.transitionDuration = animate && !prefersReducedMotion() ? '500ms' : '0ms';
    gallery.style.transform = 'translate3d(' + (-(visualIndex ?? this.#index) * 100) + '%,0,0)';
    media.forEach((slide, i) => {
      slide.setAttribute('aria-hidden', String(i !== this.#index));
      slide.inert = i !== this.#index;
      slide.querySelector('.gang-zoom-pan').style.transform = 'translate3d(0,0,0) scale(1)';
      if (i === this.#index) {
        const image = slide.querySelector('img');
        if (image) image.loading = 'eager';
      }
    });
    dialog.querySelectorAll('.gang-zoom-nav').forEach((button) => { button.hidden = media.length < 2; });
    this.refs.toolbar.hidden = !this.#isImage();
    this.#paint();
    this.dispatchEvent(new ZoomMediaSelectedEvent(this.#index));
  }

  previous() { this.#navigate(-1); }
  next() { this.#navigate(1); }
  #navigate(direction) {
    const { gallery, media } = this.refs;
    if (this.#scale > 1 || media.length < 2 || this.#wrapping) return;
    const target = this.#index + direction;
    if (target >= 0 && target < media.length) {
      this.#select(target);
      return;
    }
    const wrappedIndex = (target + media.length) % media.length;
    if (prefersReducedMotion()) {
      this.#select(wrappedIndex, false);
      return;
    }
    this.#wrapping = true;
    this.#wrapSlide = media[wrappedIndex];
    this.#wrapSlide.style.transform = 'translate3d(' + (direction * media.length * 100) + '%,0,0)';
    gallery.getBoundingClientRect();
    this.#select(wrappedIndex, true, target);
    gallery.addEventListener('transitionend', this.#onWrapTransitionEnd);
    this.#wrapTimer = window.setTimeout(() => this.#finishWrap(), 650);
  }

  #isImage() { return this.refs.media[this.#index]?.dataset.mediaType === 'image'; }
  #zoomLevels() {
    const image = this.refs.media[this.#index]?.querySelector('img');
    const width = Math.max(1, this.refs.stage.clientWidth);
    const height = Math.max(1, this.refs.stage.clientHeight);
    const ratio = image ? (image.naturalWidth || Number(image.getAttribute('width')) || 1) / (image.naturalHeight || Number(image.getAttribute('height')) || 1) : 1;
    const fittedWidth = Math.min(width, height * ratio);
    const primary = Math.min(4.4, Math.max(2, width / fittedWidth * 1.4));
    const detail = Math.min(5.4, Math.max(primary + 0.6, primary * 1.35));
    return { primary, detail };
  }
  #bounds() {
    const image = this.refs.media[this.#index]?.querySelector('img');
    const width = this.refs.stage.clientWidth;
    const height = this.refs.stage.clientHeight;
    const ratio = image ? (image.naturalWidth || Number(image.getAttribute('width')) || 1) / (image.naturalHeight || Number(image.getAttribute('height')) || 1) : 1;
    const drawnWidth = Math.min(width, height * ratio);
    const drawnHeight = drawnWidth / ratio;
    return { x: Math.max(0, (drawnWidth * this.#scale - width) / 2), y: Math.max(0, (drawnHeight * this.#scale - height) / 2) };
  }
  #clamp() {
    const bounds = this.#bounds();
    this.#x = Math.max(-bounds.x, Math.min(bounds.x, this.#x));
    this.#y = Math.max(-bounds.y, Math.min(bounds.y, this.#y));
  }

  #setZoom(scale, point) {
    if (!this.#isImage()) return;
    const nextScale = Math.max(1, Math.min(this.#zoomLevels().detail, scale));
    if (point && nextScale > this.#scale) {
      const rect = this.refs.stage.getBoundingClientRect();
      const factor = nextScale / this.#scale;
      this.#x = (this.#x - (point.x - rect.left - rect.width / 2)) * factor + (point.x - rect.left - rect.width / 2);
      this.#y = (this.#y - (point.y - rect.top - rect.height / 2)) * factor + (point.y - rect.top - rect.height / 2);
    } else {
      this.#x = this.#y = 0;
    }
    this.#scale = nextScale;
    this.#expanded = nextScale > 1;
    if (nextScale > 1) this.#loadHighResolution();
    this.#clamp();
    this.#paint();
  }
  #loadHighResolution() {
    const image = this.refs.media[this.#index]?.querySelector('img[data_max_resolution]');
    if (!image || image.dataset.zoomLoaded) return;
    image.dataset.zoomLoaded = 'true';
    image.removeAttribute('srcset');
    image.src = image.getAttribute('data_max_resolution');
  }
  zoomIn() {
    const { primary, detail } = this.#zoomLevels();
    this.#setZoom(this.#scale < primary - 0.01 ? primary : detail);
  }
  zoomOut() {
    const { primary } = this.#zoomLevels();
    this.#setZoom(this.#scale > primary + 0.01 ? primary : 1);
  }
  toggleControls() {
    this.#setZoom(this.#expanded ? 1 : this.#zoomLevels().primary);
  }
  move(direction) {
    if (this.#scale <= 1) return;
    if (direction === 'right') this.#x -= 100;
    if (direction === 'left') this.#x += 100;
    if (direction === 'down') this.#y -= 100;
    if (direction === 'up') this.#y += 100;
    this.#clamp();
    this.#paint(false);
  }

  #paint(announce = true) {
    const { dialog, toolbar, media, status } = this.refs;
    const slide = media[this.#index];
    if (!slide) return;
    slide.querySelector('.gang-zoom-pan').style.transform = 'translate3d(' + this.#x + 'px,' + this.#y + 'px,0) scale(' + this.#scale + ')';
    dialog.toggleAttribute('data-zoomed', this.#scale > 1.001);
    const { detail } = this.#zoomLevels();
    dialog.toggleAttribute('data-zoom-max', this.#scale >= detail - 0.01);
    toolbar.toggleAttribute('data-expanded', this.#expanded);
    const actions = toolbar.querySelector('.gang-zoom-actions');
    actions.inert = !this.#expanded;
    actions.setAttribute('aria-hidden', String(!this.#expanded));
    const toggle = toolbar.querySelector('.gang-zoom-toggle');
    toggle.setAttribute('aria-expanded', String(this.#expanded));
    toggle.setAttribute('aria-label', this.#expanded ? 'Reset zoom and close controls' : 'Open zoom controls and zoom in');
    const bounds = this.#bounds();
    const disabled = {
      plus: this.#scale >= detail - 0.01,
      minus: this.#scale <= 1.001,
      left: this.#x >= bounds.x - 1,
      right: this.#x <= -bounds.x + 1,
      up: this.#y >= bounds.y - 1,
      down: this.#y <= -bounds.y + 1,
    };
    toolbar.querySelectorAll('[data-control]').forEach((button) => { button.disabled = disabled[button.dataset.control]; });
    if (announce) status.textContent = 'Image ' + (this.#index + 1) + ' of ' + media.length + '. Zoom ' + Math.round(this.#scale * 100) + '%.';
  }

  handleKeyDown(event) {
    if (event.key === 'Escape') { event.preventDefault(); this.close(); return; }
    if (event.altKey || event.ctrlKey || event.metaKey) return;
    const keys = { ArrowLeft: 'left', ArrowRight: 'right', ArrowUp: 'up', ArrowDown: 'down' };
    if (keys[event.key]) {
      event.preventDefault();
      if (this.#scale > 1) this.move(keys[event.key]);
      else if (event.key === 'ArrowLeft') this.previous();
      else if (event.key === 'ArrowRight') this.next();
    } else if (event.key === '+' || event.key === '=') { event.preventDefault(); this.zoomIn(); }
    else if (event.key === '-' || event.key === '_') { event.preventDefault(); this.zoomOut(); }
    else if (event.key === '0') { event.preventDefault(); this.#setZoom(1); }
  }

  #pointerDown = (event) => {
    if (!this.#isImage() || (event.pointerType === 'mouse' && event.button !== 0)) return;
    event.preventDefault();
    this.refs.stage.setPointerCapture(event.pointerId);
    this.#pointers.set(event.pointerId, { x: event.clientX, y: event.clientY });
    this.#startGesture();
  };
  #startGesture() {
    const points = [...this.#pointers.values()];
    if (!points.length) { this.#gesture = null; return; }
    this.#gesture = {
      start: points[0], x: this.#x, y: this.#y, scale: this.#scale, moved: points.length > 1,
      distance: points.length > 1 ? Math.hypot(points[1].x - points[0].x, points[1].y - points[0].y) : 0,
      center: points.length > 1 ? { x: (points[0].x + points[1].x) / 2, y: (points[0].y + points[1].y) / 2 } : points[0],
    };
  }
  #pointerMove = (event) => {
    if (!this.#pointers.has(event.pointerId) || !this.#gesture) return;
    this.#pointers.set(event.pointerId, { x: event.clientX, y: event.clientY });
    const points = [...this.#pointers.values()];
    const gesture = this.#gesture;
    const dx = points[0].x - gesture.start.x;
    const dy = points[0].y - gesture.start.y;
    if (Math.hypot(dx, dy) > 5) gesture.moved = true;
    if (points.length > 1 && gesture.distance) {
      const distance = Math.hypot(points[1].x - points[0].x, points[1].y - points[0].y);
      const center = { x: (points[0].x + points[1].x) / 2, y: (points[0].y + points[1].y) / 2 };
      const rect = this.refs.stage.getBoundingClientRect();
      this.#scale = Math.max(1, Math.min(this.#zoomLevels().detail, gesture.scale * distance / gesture.distance));
      const factor = this.#scale / gesture.scale;
      this.#x = (gesture.x - (gesture.center.x - rect.left - rect.width / 2)) * factor + center.x - rect.left - rect.width / 2;
      this.#y = (gesture.y - (gesture.center.y - rect.top - rect.height / 2)) * factor + center.y - rect.top - rect.height / 2;
      this.#expanded = this.#scale > 1.001;
      this.#loadHighResolution();
    } else if (this.#scale > 1 && gesture.moved) {
      this.#x = gesture.x + dx;
      this.#y = gesture.y + dy;
    } else return;
    this.refs.dialog.setAttribute('data-dragging', '');
    this.#clamp();
    cancelAnimationFrame(this.#frame);
    this.#frame = requestAnimationFrame(() => this.#paint(false));
  };
  #pointerUp = (event) => {
    if (!this.#pointers.has(event.pointerId)) return;
    const gesture = this.#gesture;
    const wasPinch = this.#pointers.size > 1;
    this.#pointers.delete(event.pointerId);
    this.refs.dialog.removeAttribute('data-dragging');
    if (this.#pointers.size) {
      this.#startGesture();
      this.#gesture.moved = true;
      return;
    }
    this.#gesture = null;
    if (!gesture) return;
    if (!gesture.moved && !wasPinch) {
      const { primary, detail } = this.#zoomLevels();
      this.#setZoom(this.#scale >= detail - 0.01 ? 1 : this.#scale < primary - 0.01 ? primary : detail, { x: event.clientX, y: event.clientY });
    } else if (gesture.scale === 1 && this.#scale === 1 && !gesture.distance) {
      const dx = event.clientX - gesture.start.x;
      const dy = event.clientY - gesture.start.y;
      if (Math.abs(dx) > 50 && Math.abs(dx) > Math.abs(dy) * 1.25) this.#navigate(dx < 0 ? 1 : -1);
    }
    this.#paint();
  };
  #pointerCancel = (event) => {
    this.#pointers.delete(event.pointerId);
    this.refs.dialog.removeAttribute('data-dragging');
    this.#startGesture();
    if (this.#gesture) this.#gesture.moved = true;
  };
}

if (!customElements.get('zoom-dialog')) customElements.define('zoom-dialog', ZoomDialog);
